import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription, interval } from "rxjs";
import { take } from "rxjs/operators";
import { SpeechRecognitionService } from "../../services/speech-recognition.service";

export interface DialogData {
  title: string;
  message: string;
  data: any,
  cancelText: string,
  confirmText: string,
  additionalText?: string,
  showAdditionalButton?: boolean,
  cancelButtonTheme?: 'warn' | 'primary' | 'accent',
  needsAiDisclaimer: boolean,
  /** Seconds to wait before enabling any button; countdown shown on each button. */
  cooldownSeconds?: number
}

@Component({
  selector: 'app-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
})
export class ConfirmationDialogComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  hideConfirm: boolean = false;
  needsAiDisclaimer: boolean = false;
  /** Countdown remaining in seconds; 0 when buttons are enabled. */
  cooldownRemaining = 0;

  /**
   * Is this row a plain line of text rather than a person?
   *
   * `data.data` began as a list of PATIENTS and the template still renders those. Callers now also
   * pass plain strings — one line per document on the late-document prompt — and before this test
   * existed every one of them rendered as an empty bullet, because each branch read a property a
   * string does not have.
   */
  isTextRow(row: any): boolean {
    return typeof row === 'string';
  }

  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private speechRecognitionService: SpeechRecognitionService,
  ) {

    if (data.data) {
      data.data = data.data.patient || data.data;
    }
    if (data.confirmText === '') {
      this.hideConfirm = true;
    }

    this.needsAiDisclaimer = data.needsAiDisclaimer;
    if (data.cooldownSeconds != null && data.cooldownSeconds > 0) {
      this.cooldownRemaining = data.cooldownSeconds;
    }
  }

  ngOnInit(): void {
    if (this.cooldownRemaining > 0) {
      const seconds = this.data.cooldownSeconds ?? 0;
      this.subscriptions.push(
        interval(1000).pipe(take(seconds)).subscribe(() => {
          this.cooldownRemaining = Math.max(0, this.cooldownRemaining - 1);
        })
      );
    }
    // Subscribe to detected commands from speech recognition
    this.subscriptions.push(
      this.speechRecognitionService.getDetectedCommand().subscribe((commandData) => {
        this.handleVoiceCommand(commandData.command, commandData.payload);
      })
    );
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private handleVoiceCommand(command: string, payload?: any): void {
    if (this.cooldownRemaining > 0) return;
    switch (command) {
      case 'CONFIRM':
        this.dialogRef.close(true);
        break;
      case 'DENY':
        this.dialogRef.close(false);
        break;
    }
  }

  get confirmButtonLabel(): string {
    const base = this.data.confirmText || 'Confirm';
    if (this.cooldownRemaining > 0) {
      return `${base} (${this.cooldownRemaining})`;
    }
    return base;
  }

  get cancelButtonLabel(): string {
    const base = this.data.cancelText || 'Cancel';
    if (this.cooldownRemaining > 0) {
      return `${base} (${this.cooldownRemaining})`;
    }
    return base;
  }

  get additionalButtonLabel(): string {
    const base = this.data.additionalText ?? '';
    if (this.cooldownRemaining > 0) {
      return base ? `${base} (${this.cooldownRemaining})` : `(${this.cooldownRemaining})`;
    }
    return base;
  }

}

